import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, Search } from 'lucide-react';
import { AppError } from '@/core/errors/AppError';
import { ROUTES } from '@/core/config/routes';
import { usePagosGlobales } from '@/data/hooks/usePagosGlobales';
import { ESTADOS_PAGO, METODOS_PAGO_FILTRO } from '@/data/dto/pagoGlobal';
import { formatearFechaHora, formatearMonto } from '@/core/utils/formatters';
import { Chip } from '@/shared/components/Chip';
import { Input } from '@/shared/components/Input';
import { Pagination } from '@/shared/components/Pagination';
import { Select } from '@/shared/components/Select';
import { Table } from '@/shared/components/Table';
import { PagoDetalleDialog } from './PagoDetalleDialog';
const POR_PAGINA = 20;
function isoLocal(d) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function rangoDesdeOpcion(opcion) {
    const hoy = new Date();
    switch (opcion) {
        case 'hoy':
            return { desde: isoLocal(hoy), hasta: isoLocal(hoy) };
        case 'semana': {
            const lunes = new Date(hoy);
            const dia = (lunes.getDay() + 6) % 7;
            lunes.setDate(lunes.getDate() - dia);
            const dom = new Date(lunes);
            dom.setDate(dom.getDate() + 6);
            return { desde: isoLocal(lunes), hasta: isoLocal(dom) };
        }
        case '30d': {
            const desde = new Date(hoy);
            desde.setDate(desde.getDate() - 29);
            return { desde: isoLocal(desde), hasta: isoLocal(hoy) };
        }
        case '90d': {
            const desde = new Date(hoy);
            desde.setDate(desde.getDate() - 89);
            return { desde: isoLocal(desde), hasta: isoLocal(hoy) };
        }
        case 'todas':
        case 'rango':
            return {};
    }
}
const QUICK_CHIPS = [
    { value: 'hoy', label: 'Hoy' },
    { value: 'semana', label: 'Esta semana' },
    { value: '30d', label: 'Últimos 30d' },
    { value: '90d', label: 'Últimos 90d' },
    { value: 'todas', label: 'Todas' },
    { value: 'rango', label: 'Rango…' },
];
export function PagosPage() {
    // Default a "Últimos 30 días" — la operativa habitual es revisar lo reciente,
    // no todo el historico. Si el operador quiere mas, usa el quick chip "Todas".
    const [rangoOpcion, setRangoOpcion] = useState('30d');
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');
    const [metodo, setMetodo] = useState('');
    const [estado, setEstado] = useState('');
    const [tipo, setTipo] = useState('');
    const [busqueda, setBusqueda] = useState('');
    const [pagina, setPagina] = useState(1);
    const [detalle, setDetalle] = useState(null);
    // Cuando el chip temporal cambia (excepto 'rango'), reset de los pickers
    // y calculo del rango. Para 'rango', el usuario edita desde/hasta a mano.
    useEffect(() => {
        if (rangoOpcion === 'rango')
            return;
        const r = rangoDesdeOpcion(rangoOpcion);
        setFechaDesde(r.desde ?? '');
        setFechaHasta(r.hasta ?? '');
        setPagina(1);
    }, [rangoOpcion]);
    const { data, isLoading, isError, error, refetch } = usePagosGlobales({
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
        metodo: metodo || undefined,
        estado: estado || undefined,
        tipo: tipo || undefined,
        q: busqueda.trim() || undefined,
        pagina,
        porPagina: POR_PAGINA,
    });
    const columnas = useMemo(() => [
        {
            key: 'fecha',
            header: 'Fecha',
            cell: (p) => formatearFechaHora(p.fecha),
            width: '170px',
        },
        {
            key: 'tipo',
            header: 'Tipo',
            cell: (p) => {
                const esReverso = Boolean(p.reversaDe);
                return esReverso ? (_jsxs("span", { className: "inline-flex items-center gap-1 text-xs font-medium text-danger", children: [_jsx(ArrowDownRight, { className: "h-3.5 w-3.5" }), "Egreso"] })) : (_jsxs("span", { className: "inline-flex items-center gap-1 text-xs font-medium text-success", children: [_jsx(ArrowUpRight, { className: "h-3.5 w-3.5" }), "Ingreso"] }));
            },
            width: '110px',
        },
        {
            key: 'metodo',
            header: 'Método',
            // Si el pago tiene una pasarela especifica (MercadoPago, Stripe), la
            // mostramos. El campo `metodo` queda como fallback para los pagos
            // historicos que no tienen pasarela registrada.
            cell: (p) => p.pasarela?.etiqueta ?? p.metodo,
            width: '160px',
        },
        {
            key: 'monto',
            header: 'Monto',
            align: 'right',
            width: '140px',
            cell: (p) => {
                const esReverso = Boolean(p.reversaDe);
                return (_jsxs("span", { className: `num font-semibold ${esReverso ? 'text-danger' : 'text-success'}`, children: [esReverso ? '-' : '', formatearMonto(p.monto)] }));
            },
        },
        {
            key: 'referencia',
            header: 'Reserva',
            cell: (p) => (_jsxs("div", { className: "flex flex-col gap-0.5", children: [_jsx(Link, { to: ROUTES.reservaDetalle(p.reservaId), onClick: (e) => e.stopPropagation(), className: "font-mono text-[11px] text-primary hover:underline", children: p.reservaCodigo ?? `${p.reservaId.slice(0, 8)}…` }), p.canal ? (_jsx("span", { className: p.canal === 'WEB'
                            ? 'w-fit rounded-pill bg-info-fill px-1.5 py-0 text-[9px] font-semibold uppercase text-info'
                            : 'w-fit rounded-pill bg-surface-2 px-1.5 py-0 text-[9px] font-semibold uppercase text-text-tertiary', children: p.canal })) : null] })),
            width: '140px',
        },
        {
            key: 'estado',
            header: 'Estado',
            width: '160px',
            cell: (p) => {
                const tono = p.estado === 'REGISTRADO'
                    ? 'success'
                    : p.estado === 'REVERSADO'
                        ? 'danger'
                        : 'neutral';
                return _jsx(Chip, { tono: tono, children: p.estado });
            },
        },
    ], []);
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [_jsxs("div", { className: "flex flex-wrap items-start justify-between gap-3", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-xl font-bold text-text-primary", children: "Pagos" }), _jsx("p", { className: "text-xs text-text-secondary", children: "Movimientos financieros de todo el sistema. Ingresos en verde, egresos en rojo." })] }), data?.totales ? (_jsxs("div", { className: "flex items-center gap-4 rounded-lg border border-border-subtle bg-surface px-4 py-2 text-sm", children: [_jsxs("div", { className: "flex flex-col", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Ingresos" }), _jsx("span", { className: "num font-semibold text-success", children: formatearMonto(data.totales.ingresos) })] }), _jsx("div", { className: "h-8 w-px bg-border-subtle" }), _jsxs("div", { className: "flex flex-col", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Egresos" }), _jsx("span", { className: "num font-semibold text-danger", children: formatearMonto(data.totales.egresos) })] }), _jsx("div", { className: "h-8 w-px bg-border-subtle" }), _jsxs("div", { className: "flex flex-col", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Neto" }), _jsxs("span", { className: `num font-bold ${data.totales.neto >= 0 ? 'text-success' : 'text-danger'}`, children: [data.totales.neto >= 0 ? '+' : '', formatearMonto(data.totales.neto)] })] })] })) : null] }), _jsxs("div", { className: "flex flex-col gap-3 rounded-lg border border-border-subtle bg-surface p-3", children: [_jsx("div", { className: "flex flex-wrap items-center gap-1.5", children: QUICK_CHIPS.map((opt) => (_jsx("button", { type: "button", onClick: () => setRangoOpcion(opt.value), className: opt.value === rangoOpcion
                                ? 'rounded-pill bg-primary px-3 py-1 text-xs font-semibold text-primary-contrast'
                                : 'rounded-pill border border-border-subtle bg-surface px-3 py-1 text-xs font-medium text-text-secondary hover:bg-surface-2', children: opt.label }, opt.value))) }), _jsxs("div", { className: "flex flex-wrap items-end gap-2", children: [rangoOpcion === 'rango' ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Desde" }), _jsx(Input, { type: "date", value: fechaDesde, onChange: (e) => {
                                                    setFechaDesde(e.target.value);
                                                    setPagina(1);
                                                }, className: "w-40" })] }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Hasta" }), _jsx(Input, { type: "date", value: fechaHasta, onChange: (e) => {
                                                    setFechaHasta(e.target.value);
                                                    setPagina(1);
                                                }, className: "w-40" })] })] })) : null, _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Buscar" }), _jsxs("div", { className: "relative w-60", children: [_jsx(Search, { className: "pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-tertiary" }), _jsx(Input, { value: busqueda, onChange: (e) => {
                                                    setBusqueda(e.target.value);
                                                    setPagina(1);
                                                }, placeholder: "C\u00F3digo, nombre, email o tel\u00E9fono", className: "h-9 pl-8 text-xs" })] })] }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Tipo" }), _jsxs(Select, { value: tipo, onChange: (e) => {
                                            setTipo(e.target.value);
                                            setPagina(1);
                                        }, className: "w-36", children: [_jsx("option", { value: "", children: "Todos" }), _jsx("option", { value: "INGRESO", children: "Ingresos" }), _jsx("option", { value: "EGRESO", children: "Egresos" })] })] }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "M\u00E9todo" }), _jsxs(Select, { value: metodo, onChange: (e) => {
                                            setMetodo(e.target.value);
                                            setPagina(1);
                                        }, className: "w-44", children: [_jsx("option", { value: "", children: "Todos" }), METODOS_PAGO_FILTRO.map((m) => (_jsx("option", { value: m, children: m }, m)))] })] }), _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary", children: "Estado" }), _jsxs(Select, { value: estado, onChange: (e) => {
                                            setEstado(e.target.value);
                                            setPagina(1);
                                        }, className: "w-36", children: [_jsx("option", { value: "", children: "Todos" }), ESTADOS_PAGO.map((e) => (_jsx("option", { value: e, children: e }, e)))] })] })] })] }), isError ? (_jsxs("div", { className: "rounded-lg border border-danger bg-danger-fill px-3 py-2 text-sm text-danger", children: [error instanceof AppError ? error.mensaje : 'Error al cargar.', ' ', _jsx("button", { onClick: () => refetch(), className: "ml-2 underline", children: "Reintentar" })] })) : (_jsxs(_Fragment, { children: [_jsx(Table, { columns: columnas, items: data?.items ?? [], rowKey: (p) => p.id, onRowClick: setDetalle, cargando: isLoading, vacioTitulo: "Sin movimientos", vacioDescripcion: "No hay pagos que coincidan con los filtros." }), data ? (_jsx(Pagination, { pagina: data.paginacion.pagina, totalPaginas: data.paginacion.totalPaginas, total: data.paginacion.total, porPagina: data.paginacion.porPagina, onCambioPagina: setPagina })) : null] })), _jsx(PagoDetalleDialog, { open: Boolean(detalle), onOpenChange: (open) => !open && setDetalle(null), pago: detalle })] }));
}
